import { useMemo } from 'react';
import { useCurrencyFormatter } from '@shared/utils/currency';
import { getCurrencyDecimalPlaces, minorToMajorUnits } from '@shared/utils/currency/minorUnits';
import { floorFundBalanceToWholeUnits } from './floorFundBalanceToWholeUnits';
import type { PayoutBalance } from './cashFund.types';

/**
 * The couple's spendable fund, formatted in its own currency — e.g. `"US$3,264"`.
 *
 * Undefined until a positive balance settles, which is what callers gate on. Zero reads as no fund,
 * not "$0" — a CTA offering it is a dead end. One hook so the banner, widget and dialog agree.
 *
 * Whole units, matching the hotel tiles: "US$5,001", never "US$5,001.09".
 */
export const useFundBalanceDisplay = (balance: PayoutBalance): string | undefined => {
  const { formatCurrency } = useCurrencyFormatter();

  return useMemo(() => {
    const currencyCode = balance?.currencyCode;
    const valueInMinorUnits = balance?.valueInMinorUnits;
    if (!currencyCode || typeof valueInMinorUnits !== 'number' || valueInMinorUnits <= 0) return undefined;
    // Floored first: a bare precision of 0 would round 5001.99 UP to 5002 — more money than they have.
    const majorUnits = minorToMajorUnits(floorFundBalanceToWholeUnits(valueInMinorUnits, currencyCode), currencyCode);
    // Precision is stated, never left to the default 2: a sub-unit fund keeps its cents, and the
    // formatter's default would round KWD 0.999 UP to 1.00 — the very thing the floor exists to stop.
    const precision = Number.isInteger(majorUnits) ? 0 : getCurrencyDecimalPlaces(currencyCode);
    return formatCurrency({
      priceFloatingPointDecimalString: majorUnits.toString(),
      priceCurrencyCode: currencyCode,
      formatForm: 'auto',
      formatPrecision: precision,
    });
  }, [balance, formatCurrency]);
};
